import { useCallback, useEffect, useState } from 'react';

const MAX_VIDEO_HEIGHT = 640;

export default function useVideoPresentation({ containerRef, playbackUrl }) {
  const [aspectRatio, setAspectRatio] = useState(null);
  const [presentationSize, setPresentationSize] = useState(null);

  const handleLoadedMetadata = useCallback(event => {
    const { videoHeight, videoWidth } = event.currentTarget;
    if (!videoWidth || !videoHeight) return;

    setAspectRatio(currentRatio => currentRatio || videoWidth / videoHeight);
  }, []);

  useEffect(() => {
    setAspectRatio(null);
    setPresentationSize(null);
  }, [playbackUrl]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !aspectRatio) return undefined;

    const updatePresentationSize = () => {
      const maxWidth = container.clientWidth;
      const width = Math.min(maxWidth, MAX_VIDEO_HEIGHT * aspectRatio);
      const height = width / aspectRatio;

      setPresentationSize(currentSize => (
        currentSize?.width === width && currentSize?.height === height
          ? currentSize
          : { height, width }
      ));
    };

    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(updatePresentationSize);
    resizeObserver?.observe(container);
    window.addEventListener('resize', updatePresentationSize);
    updatePresentationSize();

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updatePresentationSize);
    };
  }, [aspectRatio, containerRef]);

  return {
    handleLoadedMetadata,
    presentationStyle: presentationSize
      ? {
        height: `${presentationSize.height}px`,
        width: `${presentationSize.width}px`,
      }
      : undefined,
  };
}
